import React, { useMemo, useState } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table'
import { putFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import MyDataTable from 'components/ui/my-data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'

type PermissionGrantee = {
  id: string
  username?: string
  nick_name?: string
}

type PermissionWithGrantees = {
  key: string
  name: string
  description: string
  users: PermissionGrantee[]
}

const parseUserIds = (text: string) =>
  [
    ...new Set(
      String(text || '')
        .split(/[\n,\s]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ]

type PermissionEditDialogProps = {
  permission: PermissionWithGrantees
  onClose: () => void
  onSaved: (updated: PermissionWithGrantees) => void
}

const PermissionEditDialog = ({
  permission,
  onClose,
  onSaved,
}: PermissionEditDialogProps) => {
  const { toast } = useToast()
  const [input, setInput] = useState(
    () => permission.users?.map((user) => user.id).join(', ') ?? '',
  )

  const { trigger, isMutating } = useSWRMutation(
    `/permissions/${permission.key}`,
    putFetcher,
    {
      onSuccess: (res: PermissionWithGrantees) => {
        onSaved(res)
        onClose()
        toast({
          title: '权限已保存',
          description: `${res.name}：${res.users?.length ?? 0} 人`,
        })
      },
      onError: (err: Error) => {
        toast({
          variant: 'destructive',
          title: '保存失败',
          description: err?.message || '请稍后重试',
        })
      },
    },
  )

  const handleSave = () => {
    trigger({ userIds: parseUserIds(input) })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>编辑权限：{permission.name}</DialogTitle>
          <DialogDescription>{permission.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="permission-user-ids">授权用户 ID</Label>
          <p className="text-sm text-muted-foreground">
            粘贴用户 ID，用逗号、空格或换行分隔
          </p>
          <Textarea
            id="permission-user-ids"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={6}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={isMutating}>
            {isMutating ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const PermissionManagement = () => {
  const { data = [], isLoading, isValidating, mutate } = useSWR<
    PermissionWithGrantees[]
  >(['/permissions'])
  const [editing, setEditing] = useState<PermissionWithGrantees | null>(null)

  const columns = useMemo<ColumnDef<PermissionWithGrantees>[]>(
    () => [
      {
        header: '权限名称',
        accessorKey: 'name',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        header: '已授权用户',
        id: 'users',
        cell: ({ row }) => {
          const users = row.original.users ?? []
          if (!users.length) {
            return <span className="text-sm text-muted-foreground">暂无</span>
          }
          return (
            <div className="flex flex-wrap gap-2 py-1">
              {users.map((user) => {
                const label =
                  user.nick_name || user.username || '未知用户'
                return (
                  <span
                    key={user.id}
                    className="inline-flex max-w-full items-center rounded-md border bg-muted/40 px-2 py-1 text-sm leading-5"
                    title={user.username || undefined}
                  >
                    <span className="truncate">{label}</span>
                  </span>
                )
              })}
            </div>
          )
        },
      },
      {
        header: '操作',
        id: 'actions',
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(row.original)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [],
  )

  const table = useReactTable({
    data,
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.key,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleSaved = (updated: PermissionWithGrantees) => {
    mutate(
      (current) =>
        (current ?? []).map((item) =>
          item.key === updated.key ? updated : item,
        ),
      { revalidate: false },
    )
  }

  if (isLoading || isValidating) {
    return <Loader>Loading permissions...</Loader>
  }

  return (
    <div>
      <Card className="h-[calc(100vh-150px)] overflow-y-auto">
        <CardContent className="pr-0">
          <MyDataTable table={table} />
        </CardContent>
      </Card>

      {editing ? (
        <PermissionEditDialog
          permission={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  )
}

export default PermissionManagement
